import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector, isDirty } from 'redux-form'
// constants
import { Forms, Modals } from 'constants/index'
// context
import { KPIContext } from 'context'
// graphql
import { PLAN_KPI_SCALE_OPTION_CUD_MUTATION } from 'graphql/planKPIs/mutations'
// hooks
import { useModalData, useShowModal } from 'hooks'
// components
import { Modal } from 'components/UI'
import ScaleOptionEditModal from '../ScaleOptionModal/Edit'
import ScaleOptionCreateModal from '../ScaleOptionModal/Create'
// view components
import KPIScaleForm from 'components/Modals/ScaleFormModal/components/Form'
// utils
import { v4 as uuid } from 'uuid'
import { handleError } from 'utils/errors/handlers'
import { map, pipe, pick } from 'lodash/fp'
import { get, omit, isEmpty, isNumber } from 'lodash'
import { generateScaleLabel, getFieldValues, getValueFromField } from 'utils'

const PAPER_PROPS = {
  style: {
    margin: 0,
    overflow: 'auto'
  }
}

const KPIScaleFormModal = ({ displayField, targetField, targetForm }) => {
  const [options, setOptions] = React.useState([])
  const [isLoading, setLoading] = React.useState(false)

  const {
    hideModal,
    modalData: { isOpen, isCreateForm }
  } = useModalData({ modalType: Modals.KPI_SCALE_MODAL })

  const { entity, refetch, update } = React.useContext(KPIContext)

  const [planKPIScaleOptionCUD] = useMutation(PLAN_KPI_SCALE_OPTION_CUD_MUTATION)

  // redux
  const dispatch = useDispatch()
  const selector = formValueSelector(targetForm)

  const { isResultTypeChanged, resultType, resultTypeScale } = useSelector((state) => ({
    isResultTypeChanged: isDirty(Forms.KPI_DETAILS_FORM)(state, ['resultInputType']),
    resultType: selector(state, 'resultInputType'),
    resultTypeScale: selector(state, 'resultTypeScale')
  }))

  const { showModal: showScaleCreateModal } = useShowModal({ modalType: Modals.SCALE_OPTION_CREATE_MODAL })
  const { showModal: showScaleEditModal } = useShowModal({ modalType: Modals.SCALE_OPTION_EDIT_MODAL })

  const scaleValue = get(entity, 'resultTypeScale')

  React.useEffect(() => {
    if (!isLoading) {
      if (!isEmpty(resultTypeScale)) {
        setOptions(resultTypeScale)
      } else if (Array.isArray(scaleValue)) {
        setOptions(scaleValue)
      }
    }
  }, [isOpen, scaleValue, resultTypeScale])

  if (!isOpen) return null

  // methods
  const changeField = (field, value) => dispatch(change(targetForm, field, value))

  const handleSubmit = async () => {
    try {
      setLoading(true)

      if (isResultTypeChanged) {
        await update({
          variables: {
            id: entity.id,
            input: {
              resultType: getValueFromField(resultType, entity.fields, 'resultInputType'),
              fieldValues: getFieldValues(entity.fields, { resultInputType: resultType })
            }
          }
        })
      }

      const input = options.map((item, index) => {
        let option = omit(item, '__typename')

        if (isNumber(option.id)) {
          option = omit(item, 'id')
        }

        return {
          ...option,
          position: index
        }
      })

      await planKPIScaleOptionCUD({
        variables: {
          planKPIId: entity.id,
          input
        }
      })

      await refetch()

      hideModal()
    } catch (error) {
      handleError(error, 'KPIScaleFormModal.handleModalSubmit')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = () => {
    changeField(
      targetField,
      options.map((option, index) => ({ ...option, position: index }))
    )
    changeField(displayField, generateScaleLabel(options))

    hideModal()
  }

  const handleFormSubmit = isCreateForm ? handleUpdate : handleSubmit

  // sortable tree
  const handleSync = ({ tree }) => pipe(map(pick(['active', 'id', 'label', 'position', 'value'])), setOptions)(tree)

  const addOption = ({ active, label, value }) => {
    const preparedValue = value && value.trim()

    const id = isCreateForm ? uuid() : new Date().getTime()

    if (preparedValue) {
      setOptions([...options, { active, label, id, value: preparedValue }])
    }
  }

  const deleteOption = (id) => () => setOptions(options.filter((option) => id !== option.id))

  const editOption = (data) => {
    const updatedOptions = options.map((option) => {
      if (option.id === data.id) {
        return { ...option, ...data }
      }

      return option
    })

    setOptions(updatedOptions)
  }

  const handleAddOption = () => showScaleCreateModal({ addOption })
  const handleEditOption = (option) => () => showScaleEditModal({ option, editOption })

  const treeSource = options.map((option) => ({
    ...option,
    title: option.label,
    parentId: null,
    canDrag: true,
    handleDeleteClick: deleteOption(option.id),
    handleEditClick: handleEditOption(option)
  }))

  return (
    <Modal
      width={'sm'}
      isOpen={isOpen}
      disableRestoreFocus
      disableBackdropClick
      disableEscapeKeyDown
      PaperProps={PAPER_PROPS}>
      <KPIScaleForm
        handleAddOption={handleAddOption}
        handleSync={handleSync}
        hideModal={hideModal}
        isLoading={isLoading}
        onSubmit={handleFormSubmit}
        treeSource={treeSource}
      />
      <ScaleOptionCreateModal />
      <ScaleOptionEditModal />
    </Modal>
  )
}

export default KPIScaleFormModal
